import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import DetailTabs from '@/pages/detail/components/DetailTabs';
import ShareBar from '@/pages/detail/components/ShareBar';
import RelatedSection from '@/pages/detail/components/RelatedSection';
import { getMovieById } from '@/lib/movieData';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { mediaTypeLabels } from '@/types/movie';

export default function DetailPage() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);

  useDocumentMeta(
    movie ? `${movie.title} - 久久热影视详情` : '内容不存在 - 久久热',
    movie
      ? `《${movie.title}》是${movie.year}年${movie.region}${mediaTypeLabels[movie.type]}，由${movie.director}执导，${movie.actors.slice(0, 2).join('、')}主演。${movie.summary}`
      : '未找到对应的影视作品，返回久久热首页继续浏览。'
  );

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-background-100 items-center justify-center">
          <i className="ri-film-line text-3xl text-foreground-400" />
        </span>
        <h1 className="mt-5 font-display text-3xl font-bold text-foreground-950">内容不存在</h1>
        <p className="mt-3 text-foreground-500">你要查看的影视作品可能已下架或链接有误。</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-4-line text-lg" />返回首页
          </Link>
          <Link to="/category/movie" className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-compass-3-line text-lg" />去分类页逛逛
          </Link>
        </div>
      </div>
    );
  }

  const metaItems = [
    { label: '类型', value: mediaTypeLabels[movie.type] },
    { label: '年份', value: String(movie.year) },
    { label: '地区', value: movie.region },
    { label: '导演', value: movie.director },
    ...(movie.episodes ? [{ label: '集数', value: `全 ${movie.episodes} 集` }] : []),
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img src={movie.poster} alt="" aria-hidden="true" className="w-full h-full object-cover object-top opacity-25 blur-xl scale-110" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60" />
        </div>

        <div className="relative w-full px-4 md:px-8 py-8 md:py-12">
          <div className="text-background-200/80">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: mediaTypeLabels[movie.type], to: `/category/${movie.type}` }, { label: movie.title }]} />
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-9">
            <div className="w-40 sm:w-48 md:w-60 shrink-0 mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img src={movie.poster} alt={`${movie.title} 海报`} title={`${movie.title} - ${mediaTypeLabels[movie.type]} - 久久热`} className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="h-7 px-3 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-xs font-semibold">{mediaTypeLabels[movie.type]}</span>
                {movie.genres.map((g) => (
                  <Link key={g} to={`/category/${movie.type}?genre=${encodeURIComponent(g)}`} className="h-7 px-3 inline-flex items-center rounded-md bg-secondary-500/20 text-background-100 text-xs font-medium hover:bg-secondary-500/40 transition-colors cursor-pointer">
                    {g}
                  </Link>
                ))}
              </div>

              <h1 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-background-50">{movie.title}</h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400 text-lg" />
                  <span className="font-display text-2xl font-bold text-accent-400">{movie.rating.toFixed(1)}</span>
                  <span className="text-xs text-background-200/70">/ 10</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm text-background-200/85">
                  <i className="ri-fire-line text-accent-400" />
                  热度 {movie.heat}
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm text-background-200/85">
                  <i className="ri-calendar-line text-accent-400" />
                  更新于 {movie.updatedAt}
                </span>
              </div>

              <p className="mt-5 text-sm md:text-base text-background-100/90 leading-relaxed max-w-3xl">{movie.summary}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <ShareBar title={movie.title} />
                <Link to={`/category/${movie.type}`} className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-50/10 border border-background-100/30 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-list-check-2 text-lg" />
                  同类内容
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info + Tabs */}
      <section className="w-full px-4 md:px-8 py-10 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-8">
          {/* Info card */}
          <aside className="rounded-lg border border-background-200 bg-background-50 p-5 h-fit">
            <h2 className="font-display text-lg font-bold text-foreground-950">作品信息</h2>
            <dl className="mt-4 space-y-3.5">
              {metaItems.map((m) => (
                <div key={m.label} className="flex gap-4">
                  <dt className="w-12 shrink-0 text-sm text-foreground-400">{m.label}</dt>
                  <dd className="text-sm text-foreground-800 font-medium">{m.value}</dd>
                </div>
              ))}
              <div className="flex gap-4">
                <dt className="w-12 shrink-0 text-sm text-foreground-400">主演</dt>
                <dd className="text-sm text-foreground-800 font-medium">{movie.actors.join('、')}</dd>
              </div>
              <div className="flex gap-4">
                <dt className="w-12 shrink-0 text-sm text-foreground-400">类型</dt>
                <dd className="text-sm text-foreground-800 font-medium">{movie.genres.join(' / ')}</dd>
              </div>
            </dl>
            <div className="mt-5 pt-5 border-t border-background-200">
              <p className="text-xs text-foreground-400 leading-relaxed">
                久久热仅提供影视资讯与推荐展示，不提供在线播放与下载服务。
              </p>
            </div>
          </aside>

          {/* Tabs */}
          <div className="min-w-0">
            <DetailTabs movie={movie} />
          </div>
        </div>
      </section>

      <RelatedSection movie={movie} />
    </div>
  );
}